import React, { useState, forwardRef, useEffect, useRef, useImperativeHandle } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import { Box, Tabs, Tab, IconButton } from '@mui/material'
import { Tooltip } from '@mui/material'
import Add from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/DeleteOutlined'
import ModeEditOutlineOutlinedIcon from '@mui/icons-material/ModeEditOutlineOutlined'
import { TabContext, TabPanel } from '@mui/lab'
import { createStyles, makeStyles } from '@mui/styles'

import { colorTypes, pageTypes, userRightTypes } from '../../../../constants'
import { DialogConfirm, DialogFieldEdit, AccessControl } from '../../../../components'
import { DebugView, Logger, LoggerTypes } from '../../../../utils'
import { FlowTab } from '../FlowTab'
import './style.scss'

const useStyles = makeStyles((theme) =>
  createStyles({
    tabScrollButtonStyle: {
      color: theme.palette.primary.main
    }
  })
)

/**
 * FlowTabs allows to create a set of tabs that can be edited and deleted.
 *
 * @param {bool} isLoading Waiting for all the API calls to be done.
 * @param {Array<object>} flowGroups Array of the flow groups available for this project's scenario.
 * @param {object} currentFlowGroup Current Flow Group selected.
 * @param {func} onTabAdd Function to execute on flow group tab add to trigger REDUX actions.
 * @param {func} onTabChange Function to execute on flow group tab change to trigger REDUX actions.
 * @param {func} onTabSave Function to execute on flow group tab save to trigger REDUX actions.
 * @param {func} onTabDelete Function to execute on flow group tab delete to trigger REDUX actions.
 * @param {func} onSimulationList Function to display the current simulations running.
 * @param {func} onSimulationLaunch Function to start a new simulation.
 * @returns {TabContext}
 */
export const FlowTabs = forwardRef(
  (
    {
      isLoading,
      flowGroups,
      currentFlowGroup,
      onTabAdd,
      onTabChange,
      onTabSave,
      onTabDelete,
      onSimulationList,
      onSimulationLaunch
    },
    ref
  ) => {
    const { t } = useTranslation()
    const styleClasses = useStyles()
    const { enqueueSnackbar } = useSnackbar()

    const [tabList, setTabList] = useState(null)
    const [tabValue, setTabValue] = useState(null)
    const [openDialogConfirm, setOpenDialogConfirm] = useState(false)
    const [openDialogUpdate, setOpenDialogUpdate] = useState(false)
    // const [activeTab, setActiveTab] = useState(0)

    // --
    const childRef = useRef()
    // let _childRef = []
    // if (tabList && tabList.length > 0) {
    //   tabList.forEach((_flowGroup) => {
    //     _childRef[`${_flowGroup.id}`] = childRef
    //   })
    // }

    const TEN = 10

    useEffect(() => {
      setTabList(flowGroups.items)
      setTabValue(`${currentFlowGroup.id}`)
    }, [flowGroups])

    useImperativeHandle(ref, () => ({
      refreshBpmn(bpmnRefresh, onComplete) {
        Logger.trace(LoggerTypes.CONTROLLER, 'FlowTabs.useImperativeHandle().refreshBpmn()', {
          bpmnRefresh,
          onComplete
        })
        // if (_childRef[`${_updateFlowGroup.id}`].current) {
        //   _childRef[`${_updateFlowGroup.id}`].current.refreshAfterSave(_updateFlowGroup, _updateElement, onComplete)
        // }
        if (childRef && childRef?.current) {
          childRef.current.refreshBpmn(bpmnRefresh, onComplete)
        } else {
          Logger.trace(LoggerTypes.CONTROLLER, 'FlowTabs.useImperativeHandle().refreshBpmn() - ERROR', {
            childRef
          })
        }
      }
    }))

    const currentTabId = currentFlowGroup?.id || parseInt(tabValue, TEN) || 0
    const currentTabName =
      currentFlowGroup?.name || (tabList && tabList[currentTabId] && tabList[currentTabId].name) || ''

    const onClickEdit = () => {
      Logger.trace(LoggerTypes.CONTROLLER, 'FlowTabs.onClickEdit()')
      setOpenDialogUpdate(!openDialogUpdate)
    }

    const addTab = (e) => {
      e.stopPropagation()
      Logger.trace(LoggerTypes.CONTROLLER, 'FlowTabs.addTab()')
      onTabAdd((flowGroupUpdated) => {
        Logger.trace(LoggerTypes.CONTROLLER, 'FlowTabs.addTab().onClickEdit()', flowGroupUpdated)
        onClickEdit()
      })

      // -- Refresh the page with the new tab and open the popin to edit its name
      // setTimeout(() => {
      //   onClickEdit(e)
      // }, 1000)
    }

    const _onTabChange = (e, _tabValue) => {
      e.stopPropagation()
      const _currentFlowGroup = flowGroups.items.filter((el) => el.id === _tabValue)[0] || {}

      Logger.trace(LoggerTypes.CONTROLLER, 'FlowTabs._onTabChange()', { e, _tabValue, _currentFlowGroup })

      if (_currentFlowGroup && _currentFlowGroup.id) {
        setTabValue(_tabValue)
        onTabChange(_currentFlowGroup)
      }
    }

    const onDialogUpdateSave = (_name) => {
      if (_name) {
        tabList.map((el) => {
          if (el.id === currentTabId) {
            el.name = _name
          }
        })
        const _currentFlowGroup = tabList.filter((el) => el && el.id === currentTabId)[0]
        // const _currentFlowGroup = tabList[activeTab]

        Logger.trace(LoggerTypes.CONTROLLER, 'FlowTabs.onDialogUpdateSave()', {
          _name,
          _currentFlowGroup,
          tabList,
          currentTabId
        })

        // setTabList(tabList)
        onTabSave(_currentFlowGroup, false) // Do not refresh the XML
      }
    }

    const onClickDelete = () => {
      Logger.trace(LoggerTypes.CONTROLLER, 'FlowTabs.onClickDelete()', { currentFlowGroup })
      setOpenDialogConfirm(!openDialogConfirm)
    }

    const onDialogConfirmDelete = (action) => {
      Logger.trace(LoggerTypes.CONTROLLER, 'FlowTabs.onDialogConfirmDelete()', { action, tabList, currentTabId })
      if (action) {
        // -- prevent to delete the first tab
        if (tabList.length === 1) {
          enqueueSnackbar(t('flowGroups.warning_only_one'), { variant: 'error' })
          return
        }
        onTabDelete(currentTabId)
      }
    }

    Logger.trace(LoggerTypes.CONTAINER, 'FlowTabs.render()', {
      currentTabId,
      currentFlowGroup,
      flowGroups,
      tabValue,
      tabList,
      isLoading
    })

    return (
      (!isLoading && tabValue !== 'undefined' && tabList && (
        <React.Fragment>
          <TabContext value={`${tabValue}`}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                ref={ref}
                value={`${tabValue}`}
                onChange={_onTabChange}
                variant="scrollable"
                scrollButtons="auto"
                TabScrollButtonProps={{ className: styleClasses.tabScrollButtonStyle }}
              >
                {tabList.map((_flowGroup) => (
                  <Tab
                    key={`flowGroup-tab-${_flowGroup.id}`}
                    value={`${_flowGroup.id}`}
                    label={
                      <div>
                        <span>{_flowGroup.name}</span>
                        <DebugView type={pageTypes.FLOW_GROUP}>{_flowGroup}</DebugView>
                      </div>
                    }
                    sx={{ flexDirection: 'row-reverse' }}
                    disableRipple={true}
                    icon={
                      <div>
                        <span>
                          <AccessControl
                            userPermissions={[userRightTypes.SCENARIO_ATTRIBUTES_EDIT]}
                            renderNoAccess={() => <></>}
                          >
                            <Tooltip ref={ref} title={t('common.update')}>
                              <ModeEditOutlineOutlinedIcon
                                onClick={onClickEdit}
                                id={_flowGroup.id}
                                color={_flowGroup.id === tabValue ? colorTypes.PRIMARY : colorTypes.SECONDARY}
                              />
                            </Tooltip>
                          </AccessControl>
                        </span>
                        <span>
                          <AccessControl
                            userPermissions={[userRightTypes.SCENARIO_ATTRIBUTES_DELETE]}
                            renderNoAccess={() => <></>}
                          >
                            <Tooltip ref={ref} title={t('common.delete')}>
                              <DeleteIcon
                                onClick={onClickDelete}
                                id={_flowGroup.id}
                                color={_flowGroup.id === tabValue ? colorTypes.PRIMARY : colorTypes.SECONDARY}
                              />
                            </Tooltip>
                          </AccessControl>
                        </span>
                      </div>
                    }
                  />
                ))}

                <div className="buttonAdd">
                  <AccessControl userPermissions={[userRightTypes.GLOBAL_ATTRIBUTES_EDIT]} renderNoAccess={() => <></>}>
                    <Tooltip ref={ref} title={t('common.create')}>
                      <IconButton onClick={addTab}>
                        <Add color="primary" />
                      </IconButton>
                    </Tooltip>
                  </AccessControl>
                </div>
              </Tabs>
            </Box>
            {tabList.map(
              (_flowGroup) =>
                _flowGroup && (
                  <TabPanel key={`custom-tab-${_flowGroup.id}`} value={`${_flowGroup.id}`} index={_flowGroup.id}>
                    <FlowTab
                      // ref={_childRef[`${_flowGroup.id}`]}
                      ref={childRef}
                      flowGroup={_flowGroup}
                      onSave={onTabSave}
                      onSimulationList={onSimulationList}
                      onSimulationLaunch={onSimulationLaunch}
                    />
                  </TabPanel>
                )
            )}
          </TabContext>
          {openDialogConfirm && (
            <DialogConfirm
              openDialog={openDialogConfirm}
              setOpenDialog={setOpenDialogConfirm}
              onClose={onDialogConfirmDelete}
              title={`${t('flowGroups.dialogs.confirm.delete.title')}: ${currentFlowGroup?.name || ''}`}
              message={t('flowGroups.dialogs.confirm.delete.message')}
            />
          )}
          {openDialogUpdate && (
            <DialogFieldEdit
              openDialog={openDialogUpdate}
              setOpenDialog={setOpenDialogUpdate}
              onSave={onDialogUpdateSave}
              value={currentTabName}
            />
          )}
        </React.Fragment>
      )) || <div></div>
    )
  }
)

FlowTabs.displayName = 'FlowTabs'

FlowTabs.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  flowGroups: PropTypes.object.isRequired,
  currentFlowGroup: PropTypes.object.isRequired,
  onTabAdd: PropTypes.func.isRequired,
  onTabChange: PropTypes.func.isRequired,
  onTabSave: PropTypes.func.isRequired,
  onTabDelete: PropTypes.func.isRequired,
  onSimulationList: PropTypes.func.isRequired,
  onSimulationLaunch: PropTypes.func.isRequired
}

FlowTabs.defaultProps = {
  isLoading: true,
  flowGroups: {},
  currentFlowGroup: {},
  onTabAdd: () => {},
  onTabChange: () => {},
  onTabSave: () => {},
  onTabDelete: () => {},
  onSimulationList: () => {},
  onSimulationLaunch: () => {}
}
